import {
  Box,
  Paper,
  Radio,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';

export type ConfirmationMode = 'auto_lock' | 'pending_ack' | 'approval_queue';

interface ModeOption {
  mode: ConfirmationMode;
  title: string;
  description: string;
  tagline: string;
}

const MODE_OPTIONS: ModeOption[] = [
  {
    mode: 'auto_lock',
    title: 'Auto lock-in',
    description: 'Bookings land confirmed on the calendar — no staff touch.',
    tagline: 'Most efficient',
  },
  {
    mode: 'pending_ack',
    title: 'Pending until acknowledged',
    description:
      'Bookings hold the slot in a pending state; staff confirm in one tap.',
    tagline: 'Default',
  },
  {
    mode: 'approval_queue',
    title: 'Approval queue',
    description: 'Every booking is reviewed before it is confirmed.',
    tagline: 'Most control',
  },
];

export interface ConfirmationModeViewProps {
  mode: ConfirmationMode;
  isLoading?: boolean;
  disabled?: boolean;
  onModeChange: (mode: ConfirmationMode) => void;
}

/** Presentational picker for the practice's booking confirmation mode (PRD D1).
 * Three-option spectrum; selection saves immediately (select pattern). */
export function ConfirmationModeView({
  mode,
  isLoading = false,
  disabled = false,
  onModeChange,
}: ConfirmationModeViewProps) {
  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          When a booking comes in
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Choose what happens once a booking lands on the calendar. Screening
          happens in the funnel above; this decides whether it still needs your
          sign-off.
        </Typography>
      </Box>

      {isLoading ? (
        <Stack spacing={1.5}>
          <Skeleton variant="rounded" height={72} />
          <Skeleton variant="rounded" height={72} />
          <Skeleton variant="rounded" height={72} />
        </Stack>
      ) : (
        <Stack spacing={1.5} role="radiogroup" aria-label="Booking confirmation mode">
          {MODE_OPTIONS.map(option => {
            const selected = option.mode === mode;
            return (
              <Paper
                key={option.mode}
                variant="outlined"
                onClick={() => {
                  if (!disabled && !selected) {
                    onModeChange(option.mode);
                  }
                }}
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  cursor: disabled ? 'default' : 'pointer',
                  borderColor: selected ? 'primary.main' : 'divider',
                  bgcolor: selected ? 'action.selected' : 'background.paper',
                  opacity: disabled && !selected ? 0.6 : 1,
                }}
              >
                <Radio
                  checked={selected}
                  disabled={disabled}
                  slotProps={{ input: { 'aria-label': option.title } }}
                  sx={{ p: 0.5 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {option.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={selected ? 'primary.main' : 'text.secondary'}
                      sx={{ fontWeight: 600, textTransform: 'uppercase' }}
                    >
                      {option.tagline}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {option.description}
                  </Typography>
                </Box>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
}
